import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const affiliationLogos = [
  {
    name: "PAMESCO",
    src: "/affiliations/pamesco-logo.png",
    href: "http://pamesco.org/portal/",
    className: "h-11 md:h-[3.2rem]",
    imageClassName: "opacity-100 max-w-[230px]",
  },
  {
    name: "WESCO",
    src: "/affiliations/wesco-logo.png",
    href: "https://wesco.gg/",
    className: "h-11 md:h-[3.15rem]",
    imageClassName: "opacity-100 max-w-[226px]",
  },
  {
    name: "International Esports Federation",
    src: "/affiliations/iesf-logo.png",
    href: "https://iesf.org/",
    className: "h-9 md:h-[2.55rem]",
    imageClassName: "opacity-100 max-w-[205px]",
  },
];

const copyByLang = {
  es: {
    title: "Quiénes Somos | Puerto Rico Esports League",
    description: "Conoce a Puerto Rico Esports League, una plataforma de esports, entretenimiento e industria creativa construida en Puerto Rico a través de medios, eventos en vivo, contenido original y alianzas estratégicas.",
    keywords: "quiénes somos PREL, Puerto Rico Esports League, esports Puerto Rico, entretenimiento gaming Puerto Rico, industria de esports Puerto Rico, gaming Puerto Rico",
    ogDescription: "PREL es una plataforma de esports, entretenimiento e industria creativa con visión de crecimiento regional e internacional.",
    back: "← Volver al inicio",
    heroEyebrow: "Quiénes Somos",
    heroTitle: "Hecho en Puerto Rico.",
    heroHighlight: "Listo para crecer.",
    heroBody:
      "Puerto Rico Esports League es una plataforma de esports y entretenimiento que combina contenido original, eventos en vivo y desarrollo cultural alrededor de la competencia. Nacimos en Puerto Rico, pero estamos construyendo un formato con capacidad de crecer mucho más allá del mercado local.",
    missionEyebrow: "Nuestra Misión",
    missionBody:
      "Profesionalizar y expandir el ecosistema de gaming y esports en Puerto Rico a través de medios, eventos, contenido y colaboraciones estratégicas.",
    visionEyebrow: "Visión",
    visionTitle: "De plataforma local a propiedad exportable.",
    visionBody:
      "PREL existe en la intersección de gaming, televisión, entretenimiento en vivo, juventud, comunidad y narrativa de marca. Esa mezcla nos permite operar no solo como liga o serie de eventos, sino como una plataforma mediática con potencial de expansión hacia audiencias hispanas en Estados Unidos, Latinoamérica y oportunidades internacionales.",
    quote: "Más que torneos: una plataforma cultural, mediática y competitiva nacida en Puerto Rico.",
    links: ["Ver eventos de la liga →", "Leer noticias y análisis →", "Ver próximos eventos →"],
    whatEyebrow: "Qué hacemos",
    whatTitle: "Una plataforma que conecta contenido, eventos y comunidad.",
    whatBody:
      "PREL no existe únicamente para organizar torneos. Su trabajo cruza producción audiovisual, experiencias presenciales, desarrollo de audiencia, cultura gaming y relaciones estratégicas con marcas e instituciones. Esa mezcla es parte de lo que hace al proyecto más útil para el ecosistema y más interesante para posibles aliados.",
    affiliationsEyebrow: "Afiliaciones internacionales",
    affiliationsTitle: "Conexiones institucionales que fortalecen a PREL",
    affiliationsBody:
      "Puerto Rico Esports League mantiene presencia y vinculación con organismos internacionales del ecosistema de esports, reforzando su proyección regional y su capacidad de representar a Puerto Rico dentro de conversaciones más amplias de desarrollo competitivo e institucional.",
    leadershipEyebrow: "Liderazgo",
    leadershipTitle: "Un liderazgo construido para crecer con el ecosistema",
    leadershipBody:
      "Puerto Rico Esports League cuenta con un liderazgo enfocado en el crecimiento estratégico, operativo y mediático del ecosistema de esports en Puerto Rico. Su estructura combina visión institucional, ejecución operativa y producción de contenido con potencial de proyección regional.",
    trajectoryEyebrow: "Trayectoria",
    trajectoryTitle: "Una evolución que ha ido más allá del formato tradicional.",
    galleryEyebrow: "Galería",
    galleryTitle: "Así se ve PREL en acción.",
    stats: [
      { value: 6, suffix: "+", label: "Años activos" },
      { value: 1200, suffix: "+", label: "Jugadores registrados" },
      { value: 50, suffix: "+", label: "Torneos celebrados" },
      { value: 8, suffix: "", label: "Juegos en competencia" },
    ],
    pillars: [
      {
        num: "01",
        title: "Medios y contenido original",
        body: "Desarrollamos contenido con lenguaje televisivo y digital para convertir gaming y cultura competitiva en una propiedad mediática con identidad propia.",
      },
      {
        num: "02",
        title: "Eventos y experiencias",
        body: "Producimos eventos en vivo, activaciones y formatos presenciales que conectan comunidad, marcas e instituciones alrededor del gaming.",
      },
      {
        num: "03",
        title: "Crecimiento del ecosistema",
        body: "Impulsamos oportunidades para talento, colaboradores y aliados estratégicos que ven en Puerto Rico una plataforma con potencial regional e internacional.",
      },
    ],
    timeline: [
      {
        year: "2019",
        title: "Consolidación inicial",
        body: "PREL empezó a tomar forma como una propuesta seria dentro del ecosistema competitivo local, combinando competencia, comunidad y producción.",
      },
      {
        year: "2020–2022",
        title: "Expansión de formatos",
        body: "La plataforma integró televisión, activaciones y colaboraciones con marcas, demostrando que podía operar más allá del formato de torneo tradicional.",
      },
      {
        year: "2023–2026",
        title: "Base para crecimiento",
        body: "PREL sigue fortaleciendo su posicionamiento como plataforma de medios, eventos y cultura gaming nacida en Puerto Rico con ambición regional.",
      },
    ],
    gallery: [
      { src: "/about-fotos/about-gallery-1.jpg", alt: "Escenario y evento PREL" },
      { src: "/about-fotos/about-gallery-2-distrito.jpg", alt: "Cobertura de competencia PREL" },
      { src: "/about-fotos/about-gallery-3-distrito.jpg", alt: "Público y tarima PREL", objectPosition: "center 38%" },
      { src: "/about-fotos/about-gallery-4.jpg", alt: "Momento de torneo PREL" },
    ],
  },
  en: {
    title: "About | Puerto Rico Esports League",
    description: "Meet Puerto Rico Esports League, an esports, entertainment, and creative-industry platform built in Puerto Rico through media, live events, original content, and strategic partnerships.",
    keywords: "about PREL, Puerto Rico Esports League, Puerto Rico esports, Puerto Rico gaming entertainment, esports industry Puerto Rico, Puerto Rico gaming",
    ogDescription: "PREL is an esports, entertainment, and creative-industry platform with regional and international growth potential.",
    back: "← Back to home",
    heroEyebrow: "About",
    heroTitle: "Built in Puerto Rico.",
    heroHighlight: "Ready to grow.",
    heroBody:
      "Puerto Rico Esports League is an esports and entertainment platform that combines original content, live events, and cultural development around competition. We were born in Puerto Rico, but we are building a format with the capacity to grow far beyond the local market.",
    missionEyebrow: "Our mission",
    missionBody:
      "To professionalize and expand Puerto Rico's gaming and esports ecosystem through media, events, content, and strategic collaborations.",
    visionEyebrow: "Vision",
    visionTitle: "From a local platform to an exportable property.",
    visionBody:
      "PREL exists at the intersection of gaming, television, live entertainment, youth culture, community, and brand storytelling. That mix allows us to operate not only as a league or an event series, but as a media platform with the potential to expand toward U.S. Hispanic audiences, Latin America, and international opportunities.",
    quote: "More than tournaments: a cultural, media, and competitive platform born in Puerto Rico.",
    links: ["View league events →", "Read news and analysis →", "View upcoming events →"],
    whatEyebrow: "What we do",
    whatTitle: "A platform that connects content, events, and community.",
    whatBody:
      "PREL does not exist only to organize tournaments. Its work spans audiovisual production, in-person experiences, audience development, gaming culture, and strategic relationships with brands and institutions. That mix is part of what makes the project more useful to the ecosystem and more interesting to potential partners.",
    affiliationsEyebrow: "International affiliations",
    affiliationsTitle: "Institutional connections that strengthen PREL",
    affiliationsBody:
      "Puerto Rico Esports League maintains presence and ties with international esports organizations, reinforcing its regional projection and its ability to represent Puerto Rico in broader conversations about competitive and institutional development.",
    leadershipEyebrow: "Leadership",
    leadershipTitle: "Leadership built to grow with the ecosystem",
    leadershipBody:
      "Puerto Rico Esports League has leadership focused on the strategic, operational, and media growth of Puerto Rico's esports ecosystem. Its structure combines institutional vision, operational execution, and content production with regional projection potential.",
    trajectoryEyebrow: "Trajectory",
    trajectoryTitle: "An evolution that moved beyond the traditional format.",
    galleryEyebrow: "Gallery",
    galleryTitle: "What PREL looks like in action.",
    stats: [
      { value: 6, suffix: "+", label: "Active years" },
      { value: 1200, suffix: "+", label: "Registered players" },
      { value: 50, suffix: "+", label: "Tournaments hosted" },
      { value: 8, suffix: "", label: "Games in competition" },
    ],
    pillars: [
      {
        num: "01",
        title: "Media and original content",
        body: "We develop television-fluent and digitally native content to turn gaming and competitive culture into a media property with its own identity.",
      },
      {
        num: "02",
        title: "Events and experiences",
        body: "We produce live events, activations, and in-person formats that connect community, brands, and institutions around gaming.",
      },
      {
        num: "03",
        title: "Ecosystem growth",
        body: "We create opportunities for talent, collaborators, and strategic partners who see Puerto Rico as a platform with regional and international potential.",
      },
    ],
    timeline: [
      {
        year: "2019",
        title: "Initial consolidation",
        body: "PREL began to take shape as a serious proposal within the local competitive ecosystem, combining competition, community, and production.",
      },
      {
        year: "2020–2022",
        title: "Format expansion",
        body: "The platform integrated television, activations, and brand collaborations, proving it could operate beyond the traditional tournament format.",
      },
      {
        year: "2023–2026",
        title: "Foundation for growth",
        body: "PREL continues strengthening its positioning as a media, events, and gaming-culture platform born in Puerto Rico with regional ambition.",
      },
    ],
    gallery: [
      { src: "/about-fotos/about-gallery-1.jpg", alt: "PREL stage and event" },
      { src: "/about-fotos/about-gallery-2-distrito.jpg", alt: "PREL competition coverage" },
      { src: "/about-fotos/about-gallery-3-distrito.jpg", alt: "PREL audience and stage", objectPosition: "center 38%" },
      { src: "/about-fotos/about-gallery-4.jpg", alt: "PREL tournament moment" },
    ],
  },
};

export default function AboutPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("keywords", copy.keywords);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/quienes-somos", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/about-fotos/about-hero.jpg", true);
    setMeta("twitter:card", "summary_large_image");

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/quienes-somos");

    const schemaId = "prel-about-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: copy.title,
          url: "https://www.puertoricoesportleague.com/quienes-somos",
          description: copy.description,
          about: {
            "@type": "Organization",
            name: "Puerto Rico Esports League",
            url: "https://www.puertoricoesportleague.com",
            logo: "https://www.puertoricoesportleague.com/logo.png",
          },
        },
        {
          "@type": "Person",
          name: "Dwight Santana",
          jobTitle: "President of Puerto Rico Esports League & Executive Producer of Puerto Rico Esports League: The Show",
          worksFor: { "@type": "Organization", name: "Puerto Rico Esports League" },
          url: "https://www.puertoricoesportleague.com/quienes-somos",
        },
        {
          "@type": "Person",
          name: "Juan Salgado",
          jobTitle: "Director of Operations",
          worksFor: { "@type": "Organization", name: "Puerto Rico Esports League" },
          url: "https://www.puertoricoesportleague.com/quienes-somos",
        },
      ],
    });

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let observer;
    const revealEls = Array.from(document.querySelectorAll(".js-reveal"));

    if (!reducedMotion) {
      revealEls.forEach((el, idx) => {
        if (idx === 0) {
          el.classList.add("opacity-100", "translate-y-0");
          return;
        }
        el.classList.add("opacity-0", "translate-y-8");
      });

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.remove("opacity-0", "translate-y-8");
            entry.target.classList.add("opacity-100", "translate-y-0");

            if (entry.target.dataset.countSection === "true") {
              entry.target.querySelectorAll("[data-count-target]").forEach((el) => {
                if (el.dataset.counted === "true") return;
                el.dataset.counted = "true";
                const target = Number(el.dataset.countTarget || 0);
                const duration = 1200;
                const start = performance.now();

                const tick = (now) => {
                  const p = Math.min((now - start) / duration, 1);
                  const eased = 1 - (1 - p) ** 3;
                  el.textContent = Math.floor(target * eased).toLocaleString("en-US");
                  if (p < 1) requestAnimationFrame(tick);
                };

                requestAnimationFrame(tick);
              });
            }
          });
        },
        { threshold: 0.16 }
      );

      revealEls.forEach((el) => observer.observe(el));
    } else {
      revealEls.forEach((el) => {
        el.classList.remove("opacity-0", "translate-y-8");
        el.classList.add("opacity-100", "translate-y-0");
      });
      document.querySelectorAll("[data-count-target]").forEach((el) => {
        el.textContent = Number(el.dataset.countTarget || 0).toLocaleString("en-US");
      });
    }

    const onScroll = () => {
      if (reducedMotion) return;
      const y = window.scrollY || 0;
      document.querySelectorAll("[data-parallax]").forEach((el) => {
        const speed = Number(el.getAttribute("data-speed") || "0.08");
        el.style.transform = `translate3d(0, ${y * speed}px, 0) scale(1.04)`;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/quienes-somos" />

      <main>
        <section className="relative min-h-[68vh] sm:min-h-[78vh] md:min-h-[88vh] flex items-end overflow-hidden">
          <img
            src="/about-fotos/about-hero.jpg"
            alt="Puerto Rico Esports League"
            data-parallax
            data-speed="0.1"
            className="absolute inset-0 w-full h-full object-cover will-change-transform"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06070A]/90 via-[#06070A]/65 to-[#06070A]/55" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(0,42,143,0.35),transparent_45%),radial-gradient(circle_at_20%_80%,rgba(206,17,38,0.28),transparent_45%)]" />

          <div className="relative z-10 w-full max-w-[1300px] mx-auto px-6 md:px-10 pb-8 sm:pb-12 md:pb-20 js-reveal transition-all duration-700">
            <a href="/" className="inline-block text-cyan-300 text-sm hover:underline mb-5 sm:mb-8">{copy.back}</a>
            <div className="grid lg:grid-cols-[minmax(0,1fr)_460px] xl:grid-cols-[minmax(0,1fr)_580px] gap-3 xl:gap-4 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-[#F5C842]">{copy.heroEyebrow}</p>
                <h1 className="mt-3 sm:mt-4 font-black leading-[0.84] text-[clamp(2.1rem,5.5vw,4.5rem)] uppercase max-w-4xl">
                  {copy.heroTitle}<br />
                  <span className="text-[#CE1126]">{copy.heroHighlight}</span>
                </h1>
                <p className="mt-4 sm:mt-6 max-w-3xl text-slate-200/90 text-base md:text-lg leading-relaxed">{copy.heroBody}</p>
              </div>

              <div className="hidden lg:flex justify-start items-center -ml-4 xl:-ml-8 -mt-28 xl:-mt-32">
                <img
                  src="/logo.png"
                  alt="Logo de Puerto Rico Esports League"
                  className="w-[450px] xl:w-[575px] h-auto object-contain drop-shadow-[0_20px_44px_rgba(0,0,0,0.42)]"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section data-count-section="true" className="border-y border-white/10 bg-[#121826] js-reveal transition-all duration-700">
          <div className="max-w-[1300px] mx-auto grid grid-cols-2 md:grid-cols-4">
            {copy.stats.map((s) => (
              <article key={s.label} className="p-6 md:p-8 border-r border-white/10 last:border-r-0 hover:bg-white/[0.03] transition-colors">
                <div className="text-4xl md:text-5xl font-black text-white">
                  <span data-count-target={s.value}>0</span>
                  {s.suffix}
                </div>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-400">{s.label}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-10 items-center js-reveal transition-all duration-700">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#111722] min-h-[420px] group">
            <img
              src="/about-fotos/about-mission-distrito.jpg"
              alt="Misión PREL"
              className="absolute inset-0 w-full h-full object-cover object-[46%_center] transition-transform duration-500 group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/82 via-black/38 to-black/10" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-cyan-300">{copy.missionEyebrow}</p>
              <p className="mt-3 text-lg md:text-2xl font-bold max-w-lg">{copy.missionBody}</p>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.visionEyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.visionTitle}</h2>
            <p className="mt-5 text-slate-300 leading-relaxed text-lg">{copy.visionBody}</p>
            <blockquote className="mt-7 border-l-4 border-[#CE1126] bg-[#111722] p-4 md:p-6 text-xl font-semibold leading-relaxed">
              “{copy.quote}”
            </blockquote>
            <div className="mt-5 flex flex-wrap gap-4 text-sm">
              <a href="/eventos" className="text-cyan-300 hover:underline">{copy.links[0]}</a>
              <a href="/blog" className="text-cyan-300 hover:underline">{copy.links[1]}</a>
              <a href="/#eventos" className="text-cyan-300 hover:underline">{copy.links[2]}</a>
            </div>
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 pb-16 js-reveal transition-all duration-700">
          <div className="border border-white/10 bg-[#111722] p-7 md:p-10 rounded-[24px]">
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.whatEyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.whatTitle}</h2>
            <p className="mt-5 max-w-4xl text-slate-300 leading-relaxed text-lg">{copy.whatBody}</p>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {copy.pillars.map((item) => (
                <article key={item.num} className="border border-white/10 bg-[#0F1220] p-6 rounded-[20px]">
                  <p className="text-4xl font-black text-white/10 leading-none">{item.num}</p>
                  <h3 className="mt-4 text-xl font-bold">{item.title}</h3>
                  <p className="mt-3 text-slate-300 leading-relaxed">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 pb-16 js-reveal transition-all duration-700">
          <div className="border border-white/10 bg-[#111722] p-7 md:p-10 rounded-[24px]">
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.affiliationsEyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.affiliationsTitle}</h2>
            <p className="mt-5 max-w-4xl text-slate-300 leading-relaxed text-lg">{copy.affiliationsBody}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {affiliationLogos.map((logo) => (
                <div key={logo.name} className="rounded-2xl border border-white/10 bg-[#0F1220] px-6 py-7 md:px-8 md:py-8" title={logo.name}>
                  <div className="h-24 md:h-28 rounded-2xl border border-white/10 bg-white flex items-center justify-center px-7 md:px-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                    <img src={logo.src} alt={logo.name} className={`${logo.className} w-auto object-contain ${logo.imageClassName || "opacity-100"}`} loading="lazy" decoding="async" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 pb-16 md:pb-24 js-reveal transition-all duration-700">
          <div className="border border-white/10 bg-[#111722] p-7 md:p-10 rounded-[24px]">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">{copy.leadershipEyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.leadershipTitle}</h2>
            <p className="mt-5 max-w-4xl text-slate-300 leading-relaxed text-lg">{copy.leadershipBody}</p>
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              <article className="border border-white/10 bg-[#0F1220] p-6 rounded-[20px]">
                <p className="text-xs uppercase tracking-[0.22em] text-cyan-300">Leadership</p>
                <h3 className="mt-3 text-2xl font-bold">Dwight Santana</h3>
                <p className="mt-3 text-slate-300 leading-relaxed">President of Puerto Rico Esports League &amp; Executive Producer of Puerto Rico Esports League: The Show</p>
              </article>
              <article className="border border-white/10 bg-[#0F1220] p-6 rounded-[20px]">
                <p className="text-xs uppercase tracking-[0.22em] text-cyan-300">Leadership</p>
                <h3 className="mt-3 text-2xl font-bold">Juan Salgado</h3>
                <p className="mt-3 text-slate-300 leading-relaxed">Director of Operations</p>
              </article>
            </div>
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 pb-16 md:pb-24 js-reveal transition-all duration-700">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.trajectoryEyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.trajectoryTitle}</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {copy.timeline.map((item) => (
              <article key={item.year} className="border border-white/10 bg-[#111722] p-6 rounded-[20px]">
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{item.year}</p>
                <h3 className="mt-3 text-xl font-bold">{item.title}</h3>
                <p className="mt-3 text-slate-300 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="max-w-[1300px] mx-auto px-6 md:px-10 pb-20 js-reveal transition-all duration-700">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.galleryEyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">{copy.galleryTitle}</h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {copy.gallery.map((item) => (
              <article key={item.src} className="overflow-hidden border border-white/10 bg-[#111722] rounded-[20px]">
                <img src={item.src} alt={item.alt} className="w-full h-64 object-cover" style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined} loading="lazy" decoding="async" />
                <p className="p-3 text-sm text-slate-300">{item.alt}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
